import React, { useState } from 'react';
import { BuyerInvite, SortDirection } from '@/types';
import { createDataTable } from '@/components/DataTable';
import { useAsyncStateWithIsLoading } from '@/utils/hooks';
import { BuyerInvitationSearchOptions, getBuyerInvitations, getBuyerInvitationsExportUrl } from '@/services/buyer-invitations';
import { CustomLink, DateTimeDisplay, LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';
import { SlideToggle } from '@ygtechnologies/rubus-ui';

export interface BuyerInvitationsTableProps {}

const DataTable = createDataTable<BuyerInvite>();
export const BuyerInvitationsTable: React.FC<BuyerInvitationsTableProps> = () => {
    const [tableState, setTableState] = useState(DataTable.getInitialState('inviteDate', SortDirection.Descending));
    const [options, setOptions] = useState<BuyerInvitationSearchOptions>({ includeAccepted: false });
    const [data, , , isLoading] = useAsyncStateWithIsLoading(
        () => getBuyerInvitations(tableState, options),
        [tableState, options],
        null
    );

    const updateOptions = (changes: Partial<BuyerInvitationSearchOptions>) => {
        setOptions((prev) => ({ ...prev, ...changes }));
    };

    if (!data) {
        return <LoadingIndicator />;
    }

    return (
        <div>
            <SlideToggle
                checked={options.includeAccepted}
                onChange={(e) => updateOptions({ includeAccepted: e.target.checked })}>
                Include accepted invitations
            </SlideToggle>
            <DataTable
                exportUrl={getBuyerInvitationsExportUrl(tableState, options)}
                hasSearchField
                isLoading={isLoading}
                data={data.items}
                onStateChange={setTableState}
                state={tableState}
                totalItemCount={data.totalCount}
                className="table style-3">
                <DataTable.Column header="Invitee email" field="inviteeEmail" sortable />
                <DataTable.Column header="Status" field="status" sortable />

                <DataTable.Column header="Invitee name" field="inviteeName" sortable />
                <DataTable.Column header="Invitee phone" field="inviteePhoneNumber" sortable />
                <DataTable.Column
                    header="Inviter profile"
                    field="inviterProfile"
                    sortable
                    renderer={({ value }) =>
                        value ? <CustomLink to={`/profiles/${value.id}`}>{value.name}</CustomLink> : <>-</>
                    }
                />
                <DataTable.Column
                    header="Inviter user"
                    field="inviterUser"
                    sortable
                    renderer={({ value }) =>
                        value ? <CustomLink to={`/users/${value.id}`}>{value.name}</CustomLink> : <>-</>
                    }
                />
                <DataTable.Column
                    header="Project"
                    field="residentialUnitContract"
                    renderer={({ value }) =>
                        value ? (
                            <CustomLink to={`/projects/${value.projectId}`}>
                                {value.projectName}
                            </CustomLink>
                        ) : (
                            <>-</>
                        )
                    }
                />
                <DataTable.Column
                    header="Unit"
                    field="residentialUnitContract"
                    renderer={({ value }) =>
                        value ? (
                            <CustomLink to={`/projects/${value.projectId}/units/${value.unitId}`}>
                                {value.unitName}
                            </CustomLink>
                        ) : (
                            <>-</>
                        )
                    }
                />
                <DataTable.Column
                    header="Invite date"
                    field="inviteDate"
                    sortable
                    renderer={({ value }) => (value ? <DateTimeDisplay value={value} /> : <>Unknown</>)}
                />
                <DataTable.Column
                    header="Accept date"
                    field="acceptDate"
                    sortable
                    renderer={({ value }) => (value ? <DateTimeDisplay value={value} /> : <>Not Accepted</>)}
                />
            </DataTable>
        </div>
    );
};
